import { TListingRoadblockConfiguration, TListingRoadblockData } from "../listingRoadblockTypesAndCodecs";
import { LISTING_INVESTOR_FIGURES_GO_LIVE } from "../investorFiguresGoLive";

// Going live is itself proof the marketing prep happened — a listing cannot reach the
// portals without photos, a description and signed-off specifics. Anything still unticked
// afterwards is a column nobody stamped, not work outstanding: on production 51 of these
// sat open behind unticked prep, the oldest live since July 2025.
//
// Deliberately NOT applied to key handling, the sign, or the investor figures. A keysafe
// can genuinely still be unfitted on a live listing, so auto-closing those would hide real
// work rather than reveal it.
const resolvedOrLive = (
    predicate: (d: TListingRoadblockData) => boolean,
): ((d: TListingRoadblockData) => boolean) =>
    (d) => predicate(d) || d.listing.live_date !== null;

export const MarketPrepReady: TListingRoadblockConfiguration = {
    type: "market_prep_ready",
    display_name: "Market prep ready",
    only_create_if_resolved: [],
    create: {
        create_condition_description: "The seller has instructed us",
        predicate: (d) => d.listing.instructed_date !== null,
    },
    checklist: [
        {
            type: "photos_uploaded",
            is_resolved_by_short_description: "Photos uploaded",
            resolved_by_party: "us",
            create: {
                create_condition_description: "Always",
                predicate: () => true,
            },
            resolve: {
                is_resolved_by_long_description: "All photos have been uploaded to the listing",
                predicate: resolvedOrLive((d) => d.listing.all_photos_uploaded !== null),
            },
        },
        {
            type: "video_tour_linked",
            is_resolved_by_short_description: "Video tour linked",
            resolved_by_party: "supplier",
            create: {
                create_condition_description: "The listing is having a video tour",
                predicate: (d) => d.listing.no_video_tour_needed === false,
            },
            resolve: {
                is_resolved_by_long_description: "The video tour URL has been added to the listing",
                predicate: resolvedOrLive((d) => d.listing.video_tour_url !== ""),
            },
        },
        {
            type: "description_proofread",
            is_resolved_by_short_description: "Description proofread",
            resolved_by_party: "us",
            create: {
                create_condition_description: "Always",
                predicate: () => true,
            },
            resolve: {
                is_resolved_by_long_description: "The description has been proofread and marked ready",
                predicate: resolvedOrLive((d) => d.listing.description_proofread_and_ready !== null),
            },
        },
        {
            type: "marketing_specifics_signed_off",
            is_resolved_by_short_description: "Specifics signed off",
            resolved_by_party: "us",
            create: {
                create_condition_description: "Always",
                predicate: () => true,
            },
            resolve: {
                is_resolved_by_long_description: "The property specifics have been signed off for marketing",
                predicate: resolvedOrLive((d) => d.listing.specifics_for_marketing_signed_off !== null),
            },
        },
        {
            type: "portal_admin_links_entered",
            is_resolved_by_short_description: "Portal links entered",
            resolved_by_party: "us",
            create: {
                create_condition_description: "Always",
                predicate: () => true,
            },
            resolve: {
                is_resolved_by_long_description: "Both the Rightmove and Zoopla admin URLs have been entered",
                predicate: resolvedOrLive((d) =>
                    d.listing.rightmove_admin_url !== ""
                    && d.listing.zoopla_admin_url !== ""),
            },
        },
        {
            type: "sign_erected",
            is_resolved_by_short_description: "Sign up",
            resolved_by_party: "supplier",
            create: {
                create_condition_description: "A sign has been ordered for the listing",
                predicate: (d) => d.has_sign_ordered && d.listing.sign_not_being_installed === false,
            },
            resolve: {
                is_resolved_by_long_description: "A sign movement records the sign as erected",
                predicate: (d) => d.has_sign_erected,
            },
        },
        {
            type: "key_strategy_chosen",
            is_resolved_by_short_description: "Key strategy chosen",
            resolved_by_party: "us",
            create: {
                create_condition_description: "Always",
                predicate: () => true,
            },
            resolve: {
                is_resolved_by_long_description: "A key holder has been chosen for the property",
                predicate: (d) => d.listing.key_holder !== "unknown",
            },
        },
        {
            type: "keysafe_fitted",
            is_resolved_by_short_description: "Keysafe fitted",
            resolved_by_party: "supplier",
            create: {
                create_condition_description: "The keys are being held in a keysafe",
                predicate: (d) => d.listing.key_holder === "keysafe",
            },
            resolve: {
                is_resolved_by_long_description: "The keysafe has been fitted at the property",
                predicate: (d) => d.listing.keysafe_has_been_fitted !== null,
            },
        },
        {
            type: "keynest_setup",
            is_resolved_by_short_description: "KeyNest set up",
            resolved_by_party: "supplier",
            create: {
                create_condition_description: "The keys are being held with KeyNest",
                predicate: (d) => d.listing.key_holder === "keynest",
            },
            resolve: {
                is_resolved_by_long_description: "KeyNest has been set up for the property",
                predicate: (d) => d.listing.keynest_has_been_setup !== null,
            },
        },
        {
            type: "keys_delivered",
            is_resolved_by_short_description: "Keys delivered",
            resolved_by_party: "us",
            create: {
                create_condition_description: "A designated person is holding the keys",
                predicate: (d) => d.listing.key_holder === "designated_person",
            },
            resolve: {
                is_resolved_by_long_description: "The keys have been delivered to the designated person",
                predicate: (d) => d.listing.keys_delivered !== null,
            },
        },
        {
            type: "viewings_strategy_chosen",
            is_resolved_by_short_description: "Viewings strategy chosen",
            resolved_by_party: "us",
            create: {
                create_condition_description: "Always",
                predicate: () => true,
            },
            resolve: {
                is_resolved_by_long_description: "It has been decided who conducts viewings",
                predicate: resolvedOrLive((d) => d.listing.viewings_conducted_by !== "unknown"),
            },
        },
        {
            type: "potential_rent_known",
            is_resolved_by_short_description: "Potential rent known",
            resolved_by_party: "us",
            create: {
                create_condition_description: "The listing was instructed on or after the investor-figures go-live",
                predicate: (d) => d.listing.instructed_date !== null
                    && d.listing.instructed_date >= LISTING_INVESTOR_FIGURES_GO_LIVE,
            },
            resolve: {
                is_resolved_by_long_description: "A monthly rental estimate has been entered for the property",
                // `> 0`, not non-null: a zero reads as "Potential rent £0 a month" to a buyer.
                predicate: (d) => (d.listing.estimated_rental_price_pcm_main_currency ?? 0) > 0,
            },
        },
        {
            type: "renovation_estimate_verified",
            is_resolved_by_short_description: "Renovation signed off",
            resolved_by_party: "us",
            create: {
                create_condition_description: "The listing was instructed on or after the investor-figures go-live",
                predicate: (d) => d.listing.instructed_date !== null
                    && d.listing.instructed_date >= LISTING_INVESTOR_FIGURES_GO_LIVE,
            },
            resolve: {
                is_resolved_by_long_description: "The renovation cost and post-renovation value have been signed off, or the property has been marked as having no renovation potential",
                // Two paths, because a single mandatory figure on a property with no
                // renovation story gets answered with a made-up one. The amounts are re-read
                // rather than trusting the stamp, so a zeroed figure re-opens the item.
                predicate: (d) =>
                    d.listing.no_renovation_potential
                    || (
                        d.listing.renovation_estimate_verified_at !== null
                        && (d.listing.renovation_cost_main_currency ?? 0) > 0
                        && (d.listing.estimated_resale_price_main_currency ?? 0) > 0
                    ),
            },
        },
        {
            type: "live_on_portals",
            is_resolved_by_short_description: "Live on the portals",
            resolved_by_party: "portal",
            create: {
                create_condition_description: "Always",
                predicate: () => true,
            },
            resolve: {
                is_resolved_by_long_description: "The listing has gone live",
                predicate: (d) => d.listing.live_date !== null,
            },
        },
    ],
};
